import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import {
  CandidateInvitationBatchState,
  ExpertRequestStage,
  type GetCandidateInvitationBatchQuery,
} from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import {
  INVITATION_BATCH_CANCELLED_NEXT_STEP,
  INVITATION_BATCH_EXPIRED_NEXT_STEP,
} from '@/core/apiErrorMessages';
import { dateFormat } from '@/utils';

import Banner, { type Notice } from './Banner';
import EmailEditor, { type EmailEditorState } from './EmailEditor';
import EmailPreview from './EmailPreview';

export type InvitationBatch = NonNullable<
  GetCandidateInvitationBatchQuery['candidateInvitationBatch']
>;

interface InvitationBatchViewProps {
  batch: InvitationBatch;
  /** The viewer's profile timezone; the browser's when unset. */
  timezone?: string | null;
  /** The mutation in flight, if any. Both buttons are disabled while one is. */
  action?: 'send' | 'cancel' | null;
  /** The outcome of the last Send or Cancel. */
  notice?: Notice | null;
  /** The draft of the email; shown, and editable, only while the batch is pending. */
  editor?: EmailEditorState;
  onSend: () => void;
  onCancel: () => void;
}

// Sending still works at these stages, but the invited experts can't apply yet.
const STAGE_WARNINGS: Partial<Record<ExpertRequestStage, string>> = {
  [ExpertRequestStage.Draft]:
    "This request is still a draft, so experts you invite can't apply until it's active.",
  [ExpertRequestStage.Closed]:
    "This request is closed, so experts you invite can't apply until it's reopened.",
};

// What to do next, shown under the title once nothing more can be sent.
const NEXT_STEPS: Partial<Record<CandidateInvitationBatchState, string>> = {
  [CandidateInvitationBatchState.Cancelled]: INVITATION_BATCH_CANCELLED_NEXT_STEP,
  [CandidateInvitationBatchState.Expired]: INVITATION_BATCH_EXPIRED_NEXT_STEP,
};

// The api re-filters the recipients of a pending batch on every read, so every
// expert prepared can drop out before it is sent.
const NO_RECIPIENTS =
  'None of these experts can be invited any more (they left the request, were rejected, or have no email on file), so there is nothing to send. Ask Claude to prepare new invitations if you still want to invite someone.';

// setTimeout fires at once for a longer delay (2^31 - 1 ms is about 24.8 days).
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

export function invitationCount(n: number): string {
  return `${n} invitation${n === 1 ? '' : 's'}`;
}

function when(iso: string, timezone?: string | null): string {
  const zone = timezone && moment.tz.zone(timezone) ? timezone : moment.tz.guess();
  const at = moment.tz(iso, zone);
  return `${at.fromNow()} (${at.format(dateFormat)} • ${at.format('h:mm a z')})`;
}

// The api reports `expired` only once it is asked again. A page opened or left
// open past expires_at shows it as expired too (useRerenderAtExpiry re-renders
// it then), rather than offering a Send that must fail.
export function displayState(batch: InvitationBatch): CandidateInvitationBatchState {
  if (
    batch.state === CandidateInvitationBatchState.Pending &&
    !moment(batch.expires_at).isAfter(moment())
  ) {
    return CandidateInvitationBatchState.Expired;
  }
  return batch.state;
}

function stateNotice(
  batch: InvitationBatch,
  state: CandidateInvitationBatchState,
  timezone?: string | null
): Notice {
  switch (state) {
    case CandidateInvitationBatchState.Sent:
      return {
        tone: 'success',
        text: batch.sent_at
          ? `Sent ${when(batch.sent_at, timezone)} by ${batch.sender.name}.`
          : `Sent by ${batch.sender.name}.`,
      };
    case CandidateInvitationBatchState.Cancelled:
      return {
        tone: 'info',
        text: batch.cancelled_at
          ? `Cancelled ${when(batch.cancelled_at, timezone)}.`
          : 'Cancelled.',
      };
    case CandidateInvitationBatchState.Expired:
      return {
        tone: 'warning',
        text: `This link expired ${when(batch.expires_at, timezone)}.`,
      };
    default:
      return {
        tone: 'info',
        text: `Nothing has been sent yet. This link expires ${when(batch.expires_at, timezone)}.`,
      };
  }
}

// Re-renders a pending batch when its link expires, so a page left open flips
// to expired. The timer re-arms after a capped delay until expires_at is reached.
function useRerenderAtExpiry(expiresAt: string, pending: boolean): void {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!pending) return undefined;
    const remaining = Math.max(moment(expiresAt).diff(moment()), 0);
    const timer = setTimeout(() => setTick((n) => n + 1), Math.min(remaining, MAX_TIMER_DELAY_MS));
    return () => clearTimeout(timer);
  }, [expiresAt, pending, tick]);
}

export default function InvitationBatchView({
  batch,
  timezone,
  action,
  notice,
  editor,
  onSend,
  onCancel,
}: InvitationBatchViewProps): JSX.Element {
  const state = displayState(batch);
  const pending = state === CandidateInvitationBatchState.Pending;
  useRerenderAtExpiry(batch.expires_at, pending);
  const status = stateNotice(batch, state, timezone);
  const nextStep = NEXT_STEPS[state];
  const { recipients } = batch;
  const nothingToSend = pending && recipients.length === 0;
  const stageWarning =
    pending && !nothingToSend ? STAGE_WARNINGS[batch.expert_request_stage] : undefined;
  const busy = !!action;
  // A sent batch shows the email it was sent with, and nothing else can change.
  const editing = pending && !nothingToSend && editor ? editor : null;

  return (
    <div className="flex flex-col gap-24 py-16">
      <header className="flex flex-col gap-8">
        <h1 className="text-on-surface hd-3">Send invitations</h1>
        {nextStep ? <p className="m-[0] text-on-surface body-1">{nextStep}</p> : null}
        <p className="m-[0] text-on-surface body-1">
          For{' '}
          <Link
            to={`/expert_request/${batch.expert_request_id}`}
            className="font-semibold text-brand-primary hover:underline"
          >
            {batch.expert_request_name}
          </Link>
        </p>
        <p className="m-[0] break-words text-on-surface body-1">
          From: <span className="font-semibold">{batch.sender.name}</span> &lt;
          {batch.sender.email}&gt;
        </p>
      </header>

      {notice?.tone === 'error' ? <Banner tone="error">{notice.text}</Banner> : null}
      {/* Always mounted so the outcome of a Send is announced: some screen readers
          skip a live region that is inserted already holding its text. */}
      <div role="status" className="flex flex-col gap-24">
        {notice && notice.tone !== 'error' ? (
          <Banner tone={notice.tone} live={false}>
            {notice.text}
          </Banner>
        ) : null}
        <Banner tone={status.tone} live={false}>
          {status.text}
        </Banner>
        {nothingToSend ? (
          <Banner tone="info" live={false}>
            {NO_RECIPIENTS}
          </Banner>
        ) : null}
        {stageWarning ? (
          <Banner tone="warning" live={false}>
            {stageWarning}
          </Banner>
        ) : null}
      </div>

      <section
        aria-labelledby="invitation-recipients"
        className="rounded-md border border-surface-border bg-surface-card"
      >
        <h2
          id="invitation-recipients"
          className="border-b border-surface-border px-16 py-12 font-semibold text-on-surface subtitle-1"
        >
          Recipients ({recipients.length})
        </h2>
        <ul>
          {recipients.map((recipient) => (
            <li
              key={recipient.candidate_id}
              className="flex flex-col gap-4 border-b border-surface-border px-16 py-12 last:border-b-[0] sm:flex-row sm:justify-between sm:gap-16"
            >
              <span className="font-semibold text-on-surface body-1">{recipient.profile_name}</span>
              <span className="min-w-0 break-all text-on-surface-muted body-1 sm:text-right">
                {recipient.emails.join(', ')}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="invitation-email"
        className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-card px-16 py-12"
      >
        <h2 id="invitation-email" className="font-semibold text-on-surface subtitle-1">
          Email
        </h2>
        {editing ? (
          <EmailEditor editor={editing} recipients={recipients} disabled={busy} />
        ) : (
          <>
            {recipients.length > 0 ? (
              <p className="m-[0] text-on-surface-muted body-2">
                Preview for {recipients[0].profile_name}. Each expert&apos;s email greets them by
                name.
              </p>
            ) : null}
            <EmailPreview subject={batch.email.subject} body={batch.email.body} />
          </>
        )}
      </section>

      {pending ? (
        <div className="flex flex-col-reverse gap-12 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={busy}
            loading={action === 'cancel'}
          >
            Cancel invitations
          </Button>
          {nothingToSend ? null : (
            // Not while the preview catches up with an edit: Send carries the
            // fields as they are, so the preview must show them first.
            <Button
              onClick={onSend}
              disabled={busy || !!editing?.problem || !!editing?.preview.updating}
              loading={action === 'send'}
            >
              Send {invitationCount(recipients.length)}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
